import { faLock, faUnlock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { twMerge } from 'tailwind-merge'
import { Button } from '../Button'

export const PROTECTED_PROPS = [
  'hasPermission',
  'onRequestPermission',
  'permissionRequestLabel',
  'protectedOverlayClassname',
  'protectedDisabledClassname',
  'loading',
] as const
export type ProtectedComponentProps = {
  children: React.ReactNode
  loading?: boolean
  hasPermission?: boolean
  ignorePermissionsIf?: boolean
  onRequestPermission?: () => void
  /** Optional override text */
  noPermissionText?: string
  permissionRequestLabel?: string

  /** Optional override text */
  protectedOverlayClassname?: string
  protectedDisabledClassname?: string

  contentVisible?: boolean
}

export function ProtectedComponent({
  children,
  ...props
}: ProtectedComponentProps) {
  const ignorePermissions =
    typeof props.ignorePermissionsIf === 'undefined'
      ? false
      : props.ignorePermissionsIf

  const hasPermission =
    typeof props.hasPermission === 'undefined' ? true : props.hasPermission

  if (ignorePermissions) return children
  if (hasPermission) return children

  if (!props.hasPermission && props.contentVisible)
    return (
      <React.Fragment>
        {children}

        {!props.hasPermission && (
          <div className='flex  items-center gap-2 absolute z-50 right-2 top-1'>
            <p
              className={twMerge(
                'text-xs min-w-fit pt-0.5 font-medium opacity-70 text-amber-900/80 dark:text-amber-100',
                !props.onRequestPermission && 'pt-1 mt-0.5',
              )}>
              {props.noPermissionText || 'Permission Required'}
            </p>
            {props.onRequestPermission && (
              <Button
                className='text-xs py-1 px-1 gap-0'
                iconPosition='right'
                icon={faUnlock}
                iconClasses='!h-3 !w-3 mx-0 px-0'
                onClick={props.onRequestPermission}>
                {props.permissionRequestLabel || 'Request'}
              </Button>
            )}
          </div>
        )}
      </React.Fragment>
    )

  return (
    <div className='relative'>
      <div
        className={twMerge(
          'relative',
          !props.hasPermission && 'pointer-events-none select-none',
          props.protectedDisabledClassname,
        )}
        aria-disabled={!props.hasPermission}>
        <div className='opacity-50 bg-amber-50 pl-8'>{children}</div>
        <div className='absolute min-w-fit w-full inset-0 z-20 flex items-center justify-center pointer-events-auto'>
          <div
            className={twMerge(
              'max-w-[520px] rounded-xl opacity-85 border border-amber-300/60  px-4 py-2 shadow-sm',
              'bg-amber-50',
              'dark:border-amber-900/60 dark:bg-amber-950/40',
              'w-full min-w-full',
              props.protectedOverlayClassname,
            )}>
            <div className='flex items-center justify-between w-full gap-3'>
              <FontAwesomeIcon
                icon={faLock}
                className='mt-0.5 h-4 w-4 text-amber-700 opacity-50 dark:text-amber-300'
              />
              <div className='flex items-center justify-between w-full min-w-fit'>
                <p className='text-sm min-w-fit opacity-50 font-medium text-amber-900 dark:text-amber-100'>
                  {props.noPermissionText || 'Permission Required'}
                </p>

                {props.onRequestPermission && (
                  <Button
                    className=' text-xs py-0.5 px-1 gap-0'
                    iconPosition='right'
                    icon={faUnlock}
                    iconClasses='!h-3 !w-3 mx-0 px-0'
                    onClick={props.onRequestPermission}>
                    {props.permissionRequestLabel || 'Request'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
